import axios from 'axios';

// Conditional GET helper for polling. On background polls it sends
// If-None-Match with the last ETag seen for a given cache key; when the server
// replies 304 (nothing changed) the body is empty and we return
// { notModified: true } so the caller can skip any state update. This keeps
// real-time polling but stops re-downloading unchanged datasets, drastically
// cutting mobile-data usage.
//
// IMPORTANT: the INITIAL load of a screen must pass conditional=false so it
// always receives the full 200 payload (a component that just mounted has no
// data yet, so a 304 would leave it empty). Only background polls pass
// conditional=true.
const etags = new Map();
// Last successful payload per cache key. On a 304 the server sends NO body, so we
// return this cached data — this lets a REMOUNTED screen (e.g. re-entering a class)
// revalidate cheaply: unchanged data => 304 (0 bytes) + instant render from cache.
const dataCache = new Map();
const MAX_KEYS = 60;

export async function pollGet(url, { key, headers = {}, params, conditional = true } = {}) {
  const cacheKey = key || url;
  const prev = etags.get(cacheKey);
  const reqHeaders = { ...headers };
  if (conditional && prev) reqHeaders['If-None-Match'] = prev;
  const res = await axios.get(url, {
    headers: reqHeaders,
    params,
    validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
  });
  if (res.status === 304) {
    if (dataCache.has(cacheKey)) {
      return { data: dataCache.get(cacheKey), notModified: true };
    }
    // 304 pero NO tenemos el cuerpo en memoria (p.ej. la cache del navegador
    // revalido por su cuenta, o es una carga recien montada). Devolver null aqui
    // dejaria la grilla EN BLANCO. Forzamos una descarga completa sin
    // If-None-Match; el cache-buster evita que el navegador vuelva a responder 304.
    const full = await axios.get(url, {
      headers: { ...headers },
      params: { ...(params || {}), _cb: Date.now() },
    });
    const etFull = full.headers && (full.headers.etag || full.headers.ETag);
    if (etFull) etags.set(cacheKey, etFull);
    dataCache.set(cacheKey, full.data);
    return { data: full.data, notModified: false };
  }
  const et = res.headers && (res.headers.etag || res.headers.ETag);
  if (et) etags.set(cacheKey, et);
  dataCache.set(cacheKey, res.data);
  // Soft cap to avoid unbounded growth over a long session (churning many
  // classes/tabs). Drops the oldest entry (Map preserves insertion order).
  if (dataCache.size > MAX_KEYS) {
    const oldest = dataCache.keys().next().value;
    dataCache.delete(oldest);
    etags.delete(oldest);
  }
  return { data: res.data, notModified: false };
}

export default pollGet;
